import React, { FunctionComponent, ReactNode } from 'react';
import { AgConversation, AgFinancingFile } from '@indigo-ag/schema';
import { Card, palettes, EllipsisLoader } from '@indigo-ag/component-module';
import orderBy from 'lodash/orderBy';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArchive, faArrowLeft, faFileAlt } from '@fortawesome/pro-regular-svg-icons';
import i18next from 'i18next';

import { createStylesHook, SortType } from 'shared';
import { date, text } from 'shared/utils';
import { TitleWrap } from '../TitleWrap/TitleWrap';

interface ConversationItemProps {
  conversationInfo: AgConversation;
  isActive?: boolean;
  onClickConversation?: () => void;
  onArchive?: (conversation: Partial<AgConversation>, event?: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  isArchivingConversation: boolean;
}

const useStyles = createStylesHook(({ typography: { pxToRem } }) => {
  const activeConversationProps = {
    backgroundColor: `${palettes.brand.gray[100]} !important`,
    marginLeft: '24px',
    marginRight: '0px'
  };
  const conversationItemProps = {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: pxToRem(14),
    color: palettes.brand.gray[800]
  };
  return {
    conversation: {
      margin: '6px 12px',
      marginLeft: 2,
      padding: '16px',
      display: 'flex',
      justifyContent: 'space-between',
      flexDirection: 'column',
      borderRadius: '0 !important',
      transition: '.2s',
      width: '95%',
      '&:hover': {
        cursor: 'pointer',
        backgroundColor: `${palettes.brand.gray[100]} !important`
      },
      '&:active': {
        backgroundColor: `${palettes.neutral.white} !important`
      }
    },
    active: {
      ...activeConversationProps
    },
    contentItem: {
      ...conversationItemProps
    },
    actionItem: {
      alignItems: 'flex-start',
      ...conversationItemProps
    },
    actionIcon: {
      cursor: 'pointer',
      '&:hover': {
        transform: 'scale(1.3)',
        transition: '.5s',
        color: palettes.brand.gray['700']
      }
    },
    title: {
      padding: '0px 10px 10px 0px',
      width: '96%'
    },
    description: {
      lineHeight: '21px',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      paddingRight: 9
    },
    receivedDate: {
      fontSize: pxToRem(14),
      color: palettes.brand.gray[700],
      whiteSpace: 'nowrap'
    },
    attachmentPreview: {
      display: 'flex',
      '& > svg': {
        marginTop: '0.2rem',
        marginRight: pxToRem(10)
      }
    }
  };
});

export const ConversationItem: FunctionComponent<ConversationItemProps> = ({
  conversationInfo: { id: conversationId, createdAt, previewMessage, roles, subject, archived: isArchived },
  isActive,
  onClickConversation,
  onArchive,
  isArchivingConversation
}) => {
  /**
   * Styles
   */
  const classes = useStyles();

  const handleConversationClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.preventDefault();
    if (onClickConversation) {
      onClickConversation();
    }
  };

  const handleArchiveConversation = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    event.stopPropagation();
    if (onArchive) {
      const conversation: Partial<AgConversation> = {
        id: conversationId,
        archived: !isArchived
      };
      onArchive(conversation, event);
    }
  };

  const renderLastMessagePreview = (previewMessage: AgConversation['previewMessage']): ReactNode => {
    if (previewMessage && previewMessage.attachments?.length) {
      const lastAttachment: AgFinancingFile | undefined = [
        ...orderBy(previewMessage.attachments, SortType.DESC)
      ].shift();
      return (
        <span className={classes.attachmentPreview}>
          <FontAwesomeIcon icon={faFileAlt} color={palettes.coastal.core.mediumBlue} />
          {lastAttachment?.documentName}
        </span>
      );
    }

    return previewMessage?.body;
  };

  const todayAt00 = date.todayAt00();

  return (
    <Card className={`${classes.conversation} ${isActive ? classes.active : ''}`} onClick={handleConversationClick}>
      <div className={classes.actionItem}>
        <div className={classes.title}>
          <TitleWrap roles={roles} subject={subject} />
        </div>
        {isArchivingConversation ? (
          <EllipsisLoader color={palettes.legacy.blue.D25} style={{ minWidth: 20 }} />
        ) : (
          <span
            title={text.toTitleCase(`${i18next.t(isArchived ? 'unarchive' : 'archive')} ${i18next.t('conversation')}`)}
          >
            <FontAwesomeIcon
              className={classes.actionIcon}
              color={palettes.legacy.blue.D25}
              onClick={handleArchiveConversation}
              icon={isArchived ? faArrowLeft : faArchive}
            />
          </span>
        )}
      </div>
      <div className={classes.contentItem}>
        <span className={classes.description}>{renderLastMessagePreview(previewMessage)}</span>
        <span className={classes.receivedDate}>
          {date.messageSentAtFormat(createdAt, todayAt00, undefined, 'MMM DD, YYYY')}
        </span>
      </div>
    </Card>
  );
};
